import React, { useState } from "react";
import { useNavigate } from "react-router";

import { useApiUrl, useDataProvider, useNotification } from "@refinedev/core";
import { ColumnDef } from "@tanstack/react-table";
import { FlaskConical, Loader2, RefreshCw } from "lucide-react";

import { ColoredBadge } from "@/components/common/colored-badge";
import {
  embeddingStatusColumn,
  enrichmentStatusColumn,
} from "@/components/common/status-columns";
import { UserAvatar } from "@/components/common/user-avatar";
import { ZoomablePhoto } from "@/components/media/photo-zoomable";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { makeTooltip } from "@/components/ui/tooltip";
import { useMembers } from "@/contexts/member-context";
import { parseUnixTimestamp } from "@/lib/dateUtils";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { IVenueData } from "@/types/venue";

function VenueRetryButton({ id }: { id: string }) {
  const [loading, setLoading] = useState(false);
  const apiUrl = useApiUrl();
  const { custom } = useDataProvider()();
  const { open } = useNotification();

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await custom?.({
        url: `${apiUrl}/venues/enrich`,
        method: "post",
        payload: { id },
      });
      open?.({
        type: "success",
        message: "Enrichment triggered",
        description: "Venue enrichment has been queued for processing.",
      });
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to trigger enrichment",
        description: toHumanErrorMessage(error),
      });
    }
    setLoading(false);
  };

  return (
    <Button
      size="sm"
      variant="outline"
      className="h-6 px-2 text-xs gap-1"
      onClick={handleClick}
      disabled={loading}
    >
      {loading ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <RefreshCw className="h-3 w-3" />
      )}
      Retry
    </Button>
  );
}

export const useVenueColumns = () => {
  const { members } = useMembers();
  const navigate = useNavigate();
  const columns = React.useMemo<ColumnDef<IVenueData>[]>(
    () => [
      {
        id: "id",
        accessorKey: "document.id",
        header: "ID",
      },
      {
        id: "artwork",
        accessorFn: (row) => row.document.media?.photos?.[0],
        size: 56,
        header: "Art",
        cell: ({ getValue }) => {
          const p: any = getValue();
          if (!p) return <></>;
          return (
            <ZoomablePhoto
              key={p.url}
              url={p.url}
              parentClassName="h-10 w-10"
            />
          );
        },
        enableSorting: false,
        enablePinning: false,
        enableColumnFilter: false,
      },
      {
        id: "name",
        // accessorKey: "document.name",
        accessorFn: (row) =>
          row.highlights.filter((h: any) => h.field === "name")[0]?.snippet ??
          row.document.name,
        header: "Name",
        minSize: 200,
        cell: function render({ getValue }: any) {
          return makeTooltip(
            <div
              className="truncate"
              dangerouslySetInnerHTML={{ __html: getValue() }}
            ></div>,
          );
        },
        enableSorting: false,
        size: 300,
      },
      {
        id: "slug",
        // accessorKey: "document.name",
        accessorFn: (row) =>
          row.highlights.filter((h: any) => h.field === "slug")[0]?.snippet ??
          row.document.slug,
        header: "Slug",
        cell: function render({ getValue }: any) {
          return makeTooltip(
            <div
              className="truncate"
              dangerouslySetInnerHTML={{ __html: getValue() }}
            ></div>,
          );
        },
        enableSorting: false,
        size: 150,
      },
      {
        id: "type",
        accessorKey: "document.type",
        header: "Type",
        cell: function render({ getValue }: any) {
          return <Badge variant="secondary">{getValue()}</Badge>;
        },
        enableSorting: false,
        size: 100,
      },
      {
        id: "sections",
        accessorKey: "document.sections",
        header: "Sections",
        cell: function render({ getValue }: any) {
          const sections: string[] = getValue() ?? [];
          return (
            <div className="flex flex-wrap gap-1">
              {sections.map((section) => (
                <Badge key={section} variant="secondary">
                  {section}
                </Badge>
              ))}
            </div>
          );
        },
        enableSorting: false,
        size: 140,
        meta: {
          filterType: "select",
          options: [
            { label: "Music", value: "Music" },
            { label: "Cinema", value: "Cinema" },
            { label: "Theater", value: "Theater" },
          ],
        },
      },
      {
        id: "geodata.name",
        accessorFn: (row) =>
          `${row?.document?.geodata?.name}, ${row?.document?.geodata?.country}`,
        header: "City",
        cell: ColoredBadge,
        minSize: 220,
      },
      {
        id: "area",
        accessorKey: "document.area",
        header: "Area",
        minSize: 150,
      },
      {
        id: "address",
        accessorKey: "document.address",
        header: "Address",
        cell: function render({ getValue }: any) {
          if (URL.canParse(getValue()))
            return (
              <a className="text-blue-500" href={getValue()} target="_blank">
                See in Maps
              </a>
            );
          return makeTooltip(
            <div className="font-extrabold truncate">{getValue()}</div>,
          );
        },
        enableSorting: false,
        size: 300,
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "document.status",
        cell: function render({ getValue }: any) {
          return <Badge variant="default">{getValue()}</Badge>;
        },
        enableSorting: false,
        size: 120,
        meta: {
          filterType: "select",
          options: [
            { label: "Complete", value: "Complete" },
            { label: "Unregistered", value: "Unregistered" },
            { label: "Requested", value: "Requested" },
          ],
        },
      },
      // Enrichment + embedding, immediately after Status — the shared
      // definitions. Venue is one of the four EMBEDDABLE entities.
      enrichmentStatusColumn<IVenueData>({
        size: 200,
        actions: ({ status, row }) => {
          if (status === "NeedsReview") {
            return (
              <Button
                size="sm"
                variant="outline"
                className="h-6 px-2 text-xs gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(
                    `/venues/enrichment/${row.document.id}`,
                    "_blank",
                  );
                }}
              >
                <FlaskConical className="h-3 w-3" />
                Review
              </Button>
            );
          }
          if (status === "Failed") {
            return <VenueRetryButton id={row.document.id} />;
          }
          return null;
        },
      }),
      embeddingStatusColumn<IVenueData>(),
      {
        id: "hide",
        accessorKey: "document.hide",
        header: "Hide",
        enableSorting: false,
        size: 60,
      },
      {
        id: "createdBy",
        header: "Created By",
        minSize: 170,
        accessorKey: "document.createdBy",
        cell: function render({ getValue }: any) {
          let value = getValue();
          const member = members.find(
            (x) =>
              x.id === value || x.adminMetadata?.relatedIds?.includes(value),
          );

          return member ? (
            <div className="p-1">
              <UserAvatar {...member} half />
            </div>
          ) : (
            value
          );
        },
        enableSorting: true,
      },
      {
        id: "createdAt",
        header: "Created At",
        accessorFn: (row) => parseUnixTimestamp(row.document.createdAt),
        cell: function render({ getValue }: any) {
          return makeTooltip(
            getValue().format("DD/MM/YYYY HH:mm"),
            getValue().format("ddd, MMM D, YYYY HH:mm"),
          );
        },
        enableSorting: true,
        meta: {
          filterType: "date",
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: function render({ row }) {
          return (
            <EditButton
              size="sm"
              hideText={true}
              recordItemId={row.original.document.id}
            />
          );
        },
        enableSorting: false,
      },
    ],
    [navigate],
  );
  return columns;
};
